import * as _ from 'lodash';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { combineLatest, from, throwError, Observable } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  pluck,
  shareReplay,
  tap,
  flatMap,
} from 'rxjs/operators';
import { isRenderingForMobileApp } from 'src/constants';
import { constants } from '../../app.constants';
import { Denomination, GiftCard, Recipient, Vendor } from '~models';
import { CheckoutService, GiftCardsService, LoaderService, ZipYiService } from '~services';
import { environment } from '../../../environments/environment';
import { SelectOptionService } from 'src/app/shared-data/select-option.service';
import { ValidationService } from 'src/app/services/validation/validation.service';
import { GiphyService } from 'src/app/services/giphy/giphy.service';
import { CheckoutStateService } from 'src/app/shared-data/checkout-state.service';
import { ZipYiContext } from '~services/biocatch/zip-yi-context.enum';

declare global {
  interface Window {
    dataLayer: {
      push: Function;
    };
  }
}

export const AMAZON_CARD_ID = 'AUBHNAMZC001';

@Component({
  selector: 'app-checkout-view',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutViewComponent implements OnInit {
  HoytsGiftCardId = 'HYBSCVARI001';

  public selectedDenomination: Denomination = new Denomination();
  public ui_code = this.ngActivatedRoute.snapshot.queryParams.ui_code; // temporarily used to hide/show vendors
  public readonly giftCard$: Observable<GiftCard> = this.ngActivatedRoute.params.pipe(
    pluck('id'), // get the id from the params
    filter(Boolean), // check it exists
    distinctUntilChanged(), // only send the card if the id changes value
    flatMap((id) => from(this.giftCardService.getCard(id, this.ui_code))), // get the card
    tap((giftCard) => {
      this.selectedDenomination = giftCard.denominations[0];
    }),
    catchError((e) => {
      window.zbm?.reportError(e);
      this.ngRouter.navigate(['/']);
      return throwError(e);
    }), // navigate away on error
    shareReplay(1),
  );

  public readonly giftCardArray$ = this.giftCard$.pipe(map((card) => [card])); // this is to rerender the component on change

  public recipient: Recipient = new Recipient();
  public console = console;

  public appPrefix = environment.appPrefix;
  public submitAttempt = false;
  public isMobileRender = isRenderingForMobileApp;
  public buyType = constants.BUY_TYPES.UNSELECTED;

  public readonly browseGiftCards$ = combineLatest(
    from(this.giftCardService.getCards()),
    from(this.giftCardService.getFeaturedCards()),
    this.giftCard$,
  ).pipe(
    map(([cards, featured, giftCard]) => {
      const featuredCards = cards.filter((card) => featured.includes(card.id));
      const totalCards = cards.filter((card) => !featured.includes(card.id));

      return featuredCards
        .concat(totalCards)
        .filter((card) => card.id !== giftCard.id)
        .slice(0, 6);
    }),
    shareReplay(1),
  );

  public readonly isPrezzee$ = this.giftCard$.pipe(map((card) => card.vendor === Vendor.Prezzee));

  public readonly isCaltexCard$ = this.giftCard$.pipe(
    map((card) => card.id === 'CALTEX_STARCASH_DIGITAL'),
  );

  // Check for Big W Giftcard
  public readonly isBigWGiftCard = this.giftCard$.pipe(
    map((card) => card.brand.toLowerCase().includes('big w')),
  );

  get id() {
    return this.ngActivatedRoute.snapshot.params.id;
  }

  get simPendingSecs() {
    return this.ngActivatedRoute.snapshot.queryParams.simPendingSecs;
  }

  constructor(
    private ngActivatedRoute: ActivatedRoute,
    private ngRouter: Router,
    private giftCardService: GiftCardsService,
    private checkoutService: CheckoutService,
    private loaderService: LoaderService,
    public readonly location: Location,
    public selectOptionService: SelectOptionService,
    public checkoutStateService: CheckoutStateService,
    private titleService: Title,
    public validationService: ValidationService,
    public giphyService: GiphyService,
    private zipYiService: ZipYiService,
  ) {}

  async ngOnInit() {
    this.loaderService.show("We're fetching your Gift Card.");
    const giftCard = _.clone(await this.giftCard$.pipe(first()).toPromise());
    this.loaderService.hide();
    const { result, checkoutId, customerId } = this.ngActivatedRoute.snapshot.queryParams;
    this.titleService.setTitle(
      `Buy ${giftCard.brand} Gift Card & Voucher Online with Zip Pay & Zip Money`,
    );

    if (result === 'approved') {
      try {
        const checkoutInfo = {
          result,
          checkoutId,
          name: this.recipient.name,
          email: this.recipient.email,
          productId: this.id,
          giftcard: giftCard,
        };

        // Cull these fields so that when encoded as base64, it is not too long
        const cullAttributes = [
          'giftcard.denominations',
          'giftcard.terms',
          'giftcard.importantContent',
        ];

        for (const attr of cullAttributes) {
          _.set(checkoutInfo, attr, undefined);
        }

        console.log({ checkoutInfo });

        const base64Encoded = btoa(unescape(encodeURIComponent(JSON.stringify(checkoutInfo))));
        // Move to card process screen
        this.ngRouter.navigate(['/finalise'], {
          queryParams: { d: base64Encoded, simPendingSecs: this.simPendingSecs },
        });
      } catch (error) {
        console.log('checkout error: ', error);
        window.zbm?.reportError(error);
        const responseError = error.error;
        if (
          responseError &&
          responseError.error &&
          responseError.error.indexOf('We are temporarily out of stock.') >= 0
        ) {
          this.ngRouter.navigate(['/error', 'We are temporarily out of stock.']);
        } else {
          this.ngRouter.navigate(['/error', "Uh oh, something's not right."]);
        }
      } finally {
        this.loaderService.hide();
      }
    } else {
      // Do nothing and stay at this screen.
      const errorMessage = `Transaction cancelled: ${JSON.stringify(result)}`;
      console.log(errorMessage);
      window.zbm?.reportError(new Error(errorMessage));
    }
    this.zipYiService.setContext(ZipYiContext.CheckoutPage);
  }

  async checkoutForFriend(isValid) {
    this.checkout(isValid, true);
  }

  async checkoutForYourself(isValid) {
    this.checkout(isValid);
  }

  async checkout(isValid, isForFriend = false) {
    if (!isValid) {
      this.submitAttempt = true;
      return;
    }

    this.checkoutStateService.setStateCheckoutBtnTriggered(true);
    try {
      const comebackUri = new URL(location.href);
      const queryParams = new URLSearchParams();
      if (this.simPendingSecs) {
        queryParams.set('simPendingSecs', this.simPendingSecs);
      }
      comebackUri.search = queryParams.toString();

      const giftCard = await this.giftCard$.pipe(first()).toPromise();
      const checkoutInfo: any = {
        price: this.selectOptionService.dataSelected,
        cardName: giftCard.brand,
        cardId: giftCard.id,
        imageUri: giftCard.image,
        name: this.recipient.name,
        email: this.recipient.email,
        vendor: giftCard.vendor,
        discount: giftCard.discount,
        isMobileRender: this.isMobileRender,
        comebackUri: comebackUri.href,
        merchantUniqueId: giftCard.merchantUniqueId,
        merchantId: giftCard.merchantId,
      };

      if (isForFriend) {
        let giftingData: any = {
          recipientEmail: this.validationService.friendForm.friendEmail.toLowerCase(),
          recipientEmailVerification:
            this.validationService.friendForm.friendEmailConfirm.toLowerCase(),
          recipientName: this.sanitiseInput(this.validationService.friendForm.friendName),
          message: this.sanitiseInput(this.validationService.friendForm.friendNote),
        };

        if (this.validationService.deliveryDate !== '') {
          giftingData.deliveryDate = this.validationService.deliveryDate;
        }

        if (this.giphyService.imageGiphy !== '') {
          giftingData.messageImageURL = this.giphyService.imageGiphy.images.original.url;
        }

        checkoutInfo.gifting = giftingData;
      }

      // Only show loading screen if it is not mobile render,
      // because web version is much slower and mobile version is pretty much instant
      if (!this.isMobileRender) {
        this.loaderService.show();
      }
      const checkoutResponse: any = await this.checkoutService.init(checkoutInfo);
      console.log('Getting the redirect for payment response: ', checkoutResponse);

      // Dealing with REST API change - the redirection_uri value could be in either places depends on API version.
      const redirect_uri = checkoutResponse.result?.redirect_uri || checkoutResponse.redirect_uri;
      this.checkoutStateService.setStateCheckoutBtnTriggered(false);
      if (redirect_uri) {
        // redirect and go to the checkout site
        window.location.href = redirect_uri;
      }
    } catch (error) {
      console.log('checkout error: ', error);
      window.zbm?.reportError(error);

      const defaultStatus: string = "Uh oh, something's not right.";
      const responseError = error.error;

      if (error instanceof HttpErrorResponse) {
        const response = error.error;
        let errorMessage: string = response.message ? `${error.status} - ${response.message}` : '';

        // Replace the vendor
        errorMessage = _.replace(errorMessage, /(vendor) \"(DigitalGlue|Prezzee)\"/gi, 'this card');

        if (responseError.code === 3 || responseError.code === 4 || responseError.code === 5) {
          this.validationService.setIsValid(false);
          this.validationService.setErrorResponse(responseError);
        } else {
          this.ngRouter.navigate(['/error', defaultStatus, errorMessage]);
        }
      } else {
        this.ngRouter.navigate(['/error', defaultStatus]);
      }

      this.checkoutStateService.setStateCheckoutBtnTriggered(false);
      this.loaderService.hide();
    }
  }

  private sanitiseInput(input?: string) {
    return input
      ?.replace(/\p{Emoji_Presentation}/gu, '')
      ?.replace(/\s+/g, ' ')
      ?.trim();
  }

  continueShopping() {
    const { utm_medium } = this.ngActivatedRoute.snapshot.queryParams;
    utm_medium && utm_medium === 'StoreDirectory'
      ? (window.location.href = `//${window.location.host}/shop`)
      : this.ngRouter.navigate(['']);
  }

  back() {
    if (this.buyType !== constants.BUY_TYPES.UNSELECTED) {
      this.buyType = constants.BUY_TYPES.UNSELECTED;
    } else {
      this.continueShopping();
    }
  }

  public viewCard($event: GiftCard) {
    this.ngRouter.navigate(['/checkout', $event.id]);
  }
}
